import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Box } from "@mui/material";
import MenuList from "../MegaMenu";
import SettingsDrawer from "../SettingsDrawer";
import { innerStructure } from "../../data";
import ColorEditor from "../ColorMenu";
import SpotlightColorEditor from "../SpotlightColorMenu";
import PositionEditor from "../EditPosition";
import { apiURL } from "../../../../../config/default";
import axios from 'axios';



const Header = () => {
  // Combined visibility state
  const [visibilityState, setVisibilityState] = useState({
    menu: false,
    variation: false,
    spotlight: false,
    colorEditor: false,
    spotlightColorEditor: false,
    position: false,
  });

  // Core state
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editState, setEditState] = useState({
    indices: null,
    levelIndex: null,
    position: null,
    parentMenuIndex: [],
    subMenuChild: null,
  });

  const [edit, setEdit] = useState({
    open: true,
    edit: "",
    title: "",
  });

  // API handlers 
const handleApiRequest = useCallback(async (method, body) => {
  try {
    let url = method === "GET"
      ? `${apiURL}header/admin/header`
      : `${apiURL}header/header`;

    const response = await axios({
      url,
      method,
      headers: { "Content-Type": "application/json" },
      data: body, // ✅ axios uses `data`, not `body`
    });

    if (response.status !== 200)
      throw new Error(`API error: ${response.statusText}`);

    return response.data;
  } catch (error) {
    console.error(`API request failed:`, error);
    throw error;
  }
}, []);


  // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await handleApiRequest("GET");
        // console.log("response",response)
        setMenuData(response?.Header?.headerData || []);
      } catch (error) {
        setMenuData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [handleApiRequest]);

  // Visibility handlers
  const resetVisibility = useCallback(() => {
    setVisibilityState({
      menu: false,
      variation: false,
      spotlight: false,
      colorEditor: false,
      spotlightColorEditor: false,
      position: false,
    });
  }, []);

  const updateVisibilityState = useCallback((updates) => {
    setVisibilityState((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Menu operation handlers
  const handleAddClick = useCallback(() => {
    updateVisibilityState({ menu: true });
    setEditState({
      indices: null,
      levelIndex: null,
      position: null,
      parentMenuIndex: [],
    });
    setEdit({ open: true, edit: "", title: "" });
  }, [updateVisibilityState]);

  const handleAddSubmenu = useCallback(
    (parentIndex, level) => {
      updateVisibilityState({
        variation: level === 2,
        menu: level !== 2,
      });
      setEditState((prev) => ({
        ...prev,
        indices: null,
        parentMenuIndex: parentIndex,
      }));
    },
    [updateVisibilityState]
  );

  // Utility functions
  const updateNestedArray = useCallback(
    (arr, indices, newValue, itemType = null) => {
      if (!indices?.length) return arr;

      const result = [...arr];
      let current = result;
      let parent = null;
      let lastKey = null;

      for (let i = 0; i < indices.length - 1; i++) {
        const index = indices[i];
        const key = i === 0 ? "submenu" : i === 1 ? itemType : null;

        if (!current[index]?.[key]) {
          if (!current[index]) return arr;
          current[index][key] = [];
        }

        parent = current;
        current = current[index][key];
        lastKey = key;
      }

      const lastIndex = indices[indices.length - 1];

      if (parent && lastKey) {
        const parentIndex = indices[indices.length - 2];
        parent[parentIndex] = {
          ...parent[parentIndex],
          [lastKey]: [
            ...parent[parentIndex][lastKey].slice(0, lastIndex),
            { ...parent[parentIndex][lastKey][lastIndex], ...newValue },
            ...parent[parentIndex][lastKey].slice(lastIndex + 1),
          ],
        };
      } else {
        result[lastIndex] = { ...result[lastIndex], ...newValue };
      }

      return result;
    },
    []
  );

  const getItemByIndices = useCallback((data, indices, levelIndex = 0) => {
    if (!indices?.length) return null;

    return indices.reduce((acc, index, i) => {
      if (!acc) return null;
      if (i === 0) return acc[index];
      if (i === 1) return acc.submenu?.[index];
      if (i === 2) {
        if (levelIndex === 1) return acc.variations?.[index];
        if (levelIndex === 2) return acc.spotItems?.[index];
      }
      return null;
    }, data);
  }, []);

  // Save handlers
  const handleSave = useCallback(
    async (newData) => {
      const { indices, parentMenuIndex } = editState;
      let updatedMenuData = [...menuData];

      if (indices) {
        const itemType = indices.length === 3 ? newData.type : "submenu";
        updatedMenuData = updateNestedArray(
          updatedMenuData,
          indices,
          newData,
          itemType
        );
      } else if (parentMenuIndex.length > 0) {
        let current = updatedMenuData;
        for (let i = 0; i < parentMenuIndex.length; i++) {
          const index = parentMenuIndex[i];
          const key =
            i === 0
              ? "submenu"
              : i === 1
              ? newData.type === "variation"
                ? "variations"
                : "spotItems"
              : null;

          if (!current[index][key]) current[index][key] = [];

          if (i === parentMenuIndex.length - 1) {
            current[index][key].push(newData);
          } else {
            current = current[index][key];
          }
        }
      } else {
        updatedMenuData.push({
          isVisible: true,
          ...newData,
          submenu: innerStructure,
        });
      }

      try {
        handleApiRequest("POST", updatedMenuData);
        setMenuData(updatedMenuData);
        resetVisibility();
        setEditState((prev) => ({
          ...prev,
          indices: null,
          parentMenuIndex: [],
        }));
      } catch (error) {
        // Handle error appropriately
      }
    },
    [menuData, editState, handleApiRequest, resetVisibility, updateNestedArray]
  );

  const handleColorSave = useCallback(
    async (colors) => {
      const { indices, levelIndex } = editState;
      let updatedMenuData = [...menuData];

      if (indices.length === 1) {
        updatedMenuData[indices[0]].colors = colors;
      } else if (indices.length === 2) {
        updatedMenuData[indices[0]].submenu[indices[1]].colors = colors;
      } else {
        const path = updatedMenuData[indices[0]]?.submenu?.[indices[1]];
        if (levelIndex === 1 && path?.variations) {
          path.variations[indices[2]].colors = colors;
        } else if (path?.spotItems) {
          path.spotItems[indices[2]].colors = colors;
        }
      }

      try {
        handleApiRequest("POST", updatedMenuData);
        setMenuData(updatedMenuData);
        resetVisibility();
        setEditState((prev) => ({ ...prev, indices: [], levelIndex: null }));
      } catch (error) {
        // Handle error appropriately
      }
    },
    [menuData, editState, handleApiRequest, resetVisibility]
  );

  // Memoized drawer configuration
  const drawerConfig = useMemo(() => {
    const { menu, variation, spotlight } = visibilityState;
    const { indices } = editState;

    return {
      showBody: spotlight && indices?.[2] > 0 && indices[2] < 4,
      showImage:
        (variation && indices?.[1] >= 0) ||
        (spotlight && indices?.[2] > 0 && indices[2] < 4),
      imageSize: {
        width: variation ? 50 : 75,
        height: variation ? 50 : 75,
      },
      type: menu ? "default" : variation ? "variations" : "spotItems",
    };
  }, [visibilityState, editState]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Box sx={{ position: "relative", display: "flex" }}>
        <Box
          sx={{
            flex: 0.95,
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            position: "relative",
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff",
          }}
        >
          <MenuList
            menuData={menuData}
            setMenuData={setMenuData}
            onEdit={(indices, levelIndex) => {
              const isSpotItem = indices.length === 3 && levelIndex === 2;
              updateVisibilityState({
                menu: indices.length !== 3,
                variation: indices.length === 3 && !isSpotItem,
                spotlight: isSpotItem,
              });
              setEditState((prev) => ({ ...prev, indices, levelIndex }));
              setEdit({ open: true, edit: "", title: "" });
            }}
            onColorEdit={(indices, levelIndex) => {
              updateVisibilityState({
                colorEditor: indices.length > 0 && levelIndex !== 2,
                spotlightColorEditor: !(indices.length > 0 && levelIndex !== 2),
              });
              setEditState((prev) => ({ ...prev, indices, levelIndex }));
              setEdit({ open: true, edit: "", title: "" });
            }}
            onDelete={async (indices) => {
              let updatedMenuData = [...menuData];
              if (indices.length === 1) {
                updatedMenuData.splice(indices[0], 1);
              } else {
                const parentIndices = indices.slice(0, -1);
                const lastIndex = indices[indices.length - 1];
                const parent = getItemByIndices(updatedMenuData, parentIndices);
                if (parent) {
                  const key =
                    indices.length === 2
                      ? "submenu"
                      : indices.length === 3
                      ? parent.variations?.[lastIndex]
                        ? "variations"
                        : "spotItems"
                      : "spotItems";
                  if (parent[key]) {
                    parent[key] = [
                      ...parent[key].slice(0, lastIndex),
                      ...parent[key].slice(lastIndex + 1),
                    ];
                  }
                }
              }
              try {
                handleApiRequest("POST", updatedMenuData);
                setMenuData(updatedMenuData);
                resetVisibility();
              } catch (error) {
                // Handle error appropriately
              }
            }}
            onAddSubmenu={handleAddSubmenu}
            handleAddClick={handleAddClick}
            setSubMenuChild={(child) =>
              setEditState((prev) => ({ ...prev, subMenuChild: child }))
            }
            onPositionEdit={(indices, levelIndex, position) => {
              updateVisibilityState({
                position: levelIndex !== 2,
              });
              setEditState((prev) => ({ ...prev, indices, position }));
              setEdit({ open: true, edit: "", title: "" });
            }}
          />
        </Box>

        {Object.values(visibilityState).some(Boolean) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: "-320px",
              zIndex: 1000,
            }}
          >
            {(visibilityState.menu ||
              visibilityState.variation ||
              visibilityState.spotlight) && (
              <SettingsDrawer
                header={
                  visibilityState.menu
                    ? "Header | Edit Settings"
                    : visibilityState.variation
                    ? "Variation Header | Edit Settings"
                    : "Spotlight Header | Edit Settings"
                }
                onSave={handleSave}
                initialData={
                  editState.indices
                    ? getItemByIndices(
                        menuData,
                        editState.indices,
                        visibilityState.menu
                          ? 0
                          : visibilityState.variation
                          ? 1
                          : 2
                      )
                    : null
                }
                config={drawerConfig}
                edit={edit}
                setEdit={setEdit}
              />
            )}

            {visibilityState.colorEditor && (
              <ColorEditor
                onSave={handleColorSave}
                initialColors={
                  editState.indices
                    ? getItemByIndices(
                        menuData,
                        editState.indices,
                        editState.levelIndex
                      )?.colors
                    : null
                }
                edit={edit}
                setEdit={setEdit}
              />
            )}

            {visibilityState.spotlightColorEditor && (
              <SpotlightColorEditor
                onSave={handleColorSave}
                data={
                  editState.indices
                    ? getItemByIndices(
                        menuData,
                        editState.indices,
                        editState.levelIndex
                      )
                    : null
                }
                edit={edit}
                setEdit={setEdit}
              />
            )}

            {visibilityState.position && (
              <PositionEditor
                initialPosition={editState.position}
                edit={edit}
                setEdit={setEdit}
                editingIndices={editState.indices}
                menuData={menuData}
                setMenuData={setMenuData}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default Header;
